import { DatePipe, formatDate } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import * as moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CalendarService } from 'src/app/_services/calendar.service';
import { ModalService } from 'src/app/_services/modal.service';
import { ProfileService } from 'src/app/_services/profile.service';
import { SidebarService } from 'src/app/_services/sidenav.service';
import { RequestService } from 'src/app/_services/request.service';
import { UtilityService } from 'src/app/_services/utility.service';
import { DeleteEventModalComponent } from './delete-event-modal/delete-event-modal.component';
import { ModalRejectedRequestComponent } from './modal-rejected-request/modal-rejected-request.component';
import { HolidayRequestsComponent } from './request-modal/HolidayRequests/HolidayRequests.component';
import { RequestPermissionComponent } from './request-modal/RequestModal/RequestPermission.component';
import { SecondStepSicknessComponent } from './request-modal/second-step/second-step-sickness/second-step-sickness.component';
import { SicknessRequestComponent } from './request-modal/SicknessRequest/SicknessRequest.component';
import { WorkplaceRequestComponent } from './request-modal/WorkplaceRequest/WorkplaceRequest.component';
import { ReviewModalComponent } from './review-modal/review-modal.component';
import { UserCalendarComponent } from './user-calendar/user-calendar.component';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit, AfterViewChecked, OnDestroy {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public readonly sidebarService: SidebarService = inject(SidebarService);
  private readonly nzModalService: NzModalService = inject(NzModalService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly calendarService: CalendarService = inject(CalendarService);
  private readonly requestService: RequestService = inject(RequestService);
  public readonly utilityService: UtilityService = inject(UtilityService);
  private readonly titleService: UtilityService = inject(UtilityService);
  private readonly title: Title = inject(Title);
  private readonly datePipe: DatePipe = inject(DatePipe);
  private readonly modalService: ModalService = inject(ModalService);

  @ViewChildren('firstColumn') allColumns!: QueryList<ElementRef>;

  currentUser: any;
  canClick = false;
  currentDate: Date = new Date();
  listOfColumns: any[] = [];
  users: any[] = [];
  activeUsers: any[] = [];
  private columnWidthChecked = false;
  columnUserWidth: number = 0;
  profileData: any;
  userRole: any;
  usersSelected: any[] = [];
  usersSelectedSubject = new Subject<any[]>();
  selectedWeek: { startWeek: Date; endWeek: Date } = {
    startWeek: moment().startOf('isoWeek').toDate(),
    endWeek: moment().startOf('isoWeek').isoWeekday(5).toDate()
  };
  titleRequest: string;
  teamsIdSelected: number[] = [];
  roleIdSelected: number[] = [];
  eventsIdSelected: number[] = [];
  private readonly destroy$: Subject<void> = new Subject<void>();
  isSideOpen: boolean = false;
  today = formatDate(new Date(), 'YYYY-MM-dd', 'en-US');
  monthSelected: Date = new Date();
  notifications: any = null;
  calendarArea: 'all' | 'production' | 'administration' = 'production';
  calendarAreaOptions = [
    { label: 'Tutti', value: 'all' },
    { label: 'Produzione', value: 'production' },
    { label: 'Amministrazione', value: 'administration' }
  ];

  requestTypes = [
    {
      label: 'Richiesta Permesso',
      type: 'Permesso',
      icon: 'schedule.svg',
      alt: 'scheduler'
    },
    {
      label: 'Richiesta Ferie',
      type: 'Ferie',
      icon: 'Vector.svg',
      alt: 'Vector'
    },
    {
      label: 'Malattia',
      type: 'Malattia',
      icon: 'sickness.svg',
      alt: 'sickness'
    },
    {
      label: 'Gestisci presenze',
      type: 'Luogo di lavoro',
      icon: 'request_smart.svg',
      alt: 'request_smart'
    }
  ];
  adminRequestTypes = this.requestTypes.filter(
    requestType => requestType.type !== 'Luogo di lavoro'
  );
  userUuid: string;
  private readonly partTimePendingRequestCache = new Map<string, any>();
  private readonly loadingPartTimePendingRequests = new Set<string>();

  constructor() {}

  ngOnInit() {
    this.titleService.setTitle('Calendario');
    this.title.setTitle('Calendario | Sviluppo Personale');
    this.sidebarService.toggleSidenav(true);
    this.profileService.getUserRoleAsync().then(profileData => {
      this.profileData = profileData;
      this.userRole = this.profileData.roles[0].name;
      this.calendarArea = this.getDefaultCalendarArea();
      this.initializeCalendar();
    });

    this.usersSelectedSubject
      .pipe(distinctUntilChanged(), debounceTime(1000))
      .subscribe(() => {
        this.getUsersCalendar(this.selectedWeek.startWeek);
      });
  }

  private initializeCalendar() {
    this.getActiveUsers();

    if (history.state.user_uuid) {
      const state = history.state;
      const normalizedStateDate = moment(
        state.date,
        ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601],
        true
      );
      const targetDate = normalizedStateDate.isValid()
        ? normalizedStateDate.toDate()
        : new Date(state.date);

      this.usersSelected = [state.user_uuid];
      this.monthSelected = targetDate;
      this.selectedWeek.startWeek = targetDate;
      this.getUsersCalendar(targetDate);
      // vado a svuotare lo state
      history.replaceState({}, document.title);
      return;
    }

    this.getUsersCalendar();
  }

  getDefaultCalendarArea(): 'all' | 'production' | 'administration' {
    if (this.userRole == 'admin') {
      return 'all';
    }

    return this.isAdministrativeUser() ? 'administration' : 'production';
  }

  isAdministrativeUser(): boolean {
    const hasHrRole = this.userRole == 'hr_manager';
    const hasAdministrativeAssignmentRole =
      this.profileData?.assignmentroles?.some(
        (role: any) =>
          String(role?.alias ?? '').toLowerCase() == 'amministrativo'
      );

    return hasHrRole || hasAdministrativeAssignmentRole;
  }

  getVisibleCalendarAreaOptions() {
    return this.calendarAreaOptions.filter(
      option => option.value != 'all' || this.userRole == 'admin'
    );
  }

  onCalendarAreaChange(area: string) {
    if (!['all', 'production', 'administration'].includes(area)) return;
    if (this.calendarArea == area) return;

    this.calendarArea = area as 'all' | 'production' | 'administration';
    this.usersSelected = [];
    this.getActiveUsers();
    this.getUsersCalendar(this.selectedWeek.startWeek);
  }

  ngAfterViewChecked() {
    if (!this.columnWidthChecked) {
      this.setColumnWidthIfAvailable();
    }
  }

  private setColumnWidthIfAvailable() {
    const firstColumn = this.allColumns.first;
    if (firstColumn) {
      const width = firstColumn.nativeElement.offsetWidth;
      if (width > 0) {
        this.columnUserWidth = width;
        this.columnWidthChecked = true;
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Metodo che serve per recuperare gli utenti attivi
   */
  getActiveUsers() {
    this.calendarService
      .getActiveUsers(this.calendarArea)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.activeUsers = res;
          this.currentUser = this.activeUsers.find(
            x => x.id === this.profileData.id
          );
        }
      });
  }

  /**
   * Metodo che serve per recuperare il calendario settimanale di ogni utente
   * @param date data di riferimento
   */
  getUsersCalendar(date?: Date) {
    const startOfWeek = moment(date ?? this.currentDate).startOf('isoWeek');
    const dataStartWeek = moment(startOfWeek).format('YYYY/MM/DD');
    this.calendarService
      .getCalendarUsers(
        dataStartWeek,
        this.usersSelected,
        this.teamsIdSelected,
        this.roleIdSelected,
        this.eventsIdSelected,
        undefined,
        this.calendarArea
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.users = res.users;
          this.listOfColumns = res.listOfColumns;
          if (!this.columnUserWidth) this.updateWidth();
          this.getNotifications(date);
        }
      });
  }

  /**
   * funzione che serve per calcolare quanti giorni totali occupa la richiesta
   * @param start data inizio
   * @param end data fine
   * @returns number
   */
  calculateTotalDays(start: string, end: string): number {
    const startDate = new Date(start);
    const endDate = new Date(end);

    // Differenza in millisecondi
    const timeDiff = endDate.getTime() - startDate.getTime();

    // Converti in giorni e aggiungi 1 per includere entrambi i giorni
    const daysDiff = timeDiff / (1000 * 3600 * 24) + 1;

    return Math.round(daysDiff); // Arrotonda per evitare decimali
  }

  /**
   * Funzione che serve per calcolare quanti giorni mancano all'inizio della richiesta
   * @param start data inizio
   * @param end data fine
   * @returns number
   */
  calculateStartRequest(start: string, end: string): number {
    const fixedDate = new Date(start);
    const otherDate = new Date(end);

    // Calcola la differenza in millisecondi
    const timeDiff = otherDate.getTime() - fixedDate.getTime();

    // Converti in giorni (arrotondando per evitare problemi con l'ora legale)
    const daysDiff = Math.round(timeDiff / (1000 * 3600 * 24));

    return daysDiff;
  }

  /**
   * Restituisce le tipologie di richiesta disponibili per un utente in una data specifica.
   * Admin e HR possono censire anche malattie passate per conto degli utenti.
   */
  getAvailableRequestTypes(user: any, date: string) {
    if (this.isPartTimeNonWorkingDate(user, date)) {
      return this.requestTypes.filter(
        requestType => requestType.type === 'Luogo di lavoro'
      );
    }

    const madeRequests = this.getUserRequestsForDate(user, date);
    const isToday =
      new Date(date).toDateString() === this.currentDate.toDateString();
    const canCreatePastSickness = this.canCreateRequestsForUsers(user);

    // Se esiste già "Malattia", non mostra nulla
    if (madeRequests.includes('Malattia')) {
      return [];
    }

    // Se esiste "Ferie", mostra solo "Malattia" per oggi o per admin/hr
    if (madeRequests.includes('Ferie')) {
      return isToday || canCreatePastSickness
        ? this.requestTypes.filter(r => r.type === 'Malattia')
        : [];
    }

    // Gli utenti normali possono richiedere malattia solo oggi; admin/hr anche nel passato
    return this.requestTypes.filter(r =>
      r.type === 'Malattia' ? isToday || canCreatePastSickness : true
    );
  }

  getAdminRequestTypes(user: any, date: string) {
    if (this.isPartTimeNonWorkingDate(user, date)) {
      return this.requestTypes.filter(
        requestType => requestType.type === 'Luogo di lavoro'
      );
    }

    const availableTypes = this.getAvailableRequestTypes(user, date).map(
      requestType => requestType.type
    );

    return this.adminRequestTypes.filter(requestType =>
      availableTypes.includes(requestType.type)
    );
  }

  canCreateReviewForUsers(): boolean {
    return this.userRole == 'admin';
  }

  canCreateRequestsForUsers(user?: any): boolean {
    if (this.userRole == 'admin') {
      return true;
    }

    if (this.userRole == 'hr_manager') {
      return user ? user.id !== this.profileData?.id : true;
    }

    return false;
  }

  isHolidayDate(date: string): boolean {
    return this.listOfColumns.some(
      column => column.date === date && !!column.holiday
    );
  }

  /**
   * Estrae i tipi di richiesta attivi in una data specifica, considerando anche gli eventi che
   * si estendono su più giorni (es. ferie dal 3 al 5 includono anche il 4).
   *
   * @param user - Oggetto utente che contiene l'array `days`, ognuno con richieste
   * @param date - La data da verificare (stringa, formato YYYY-MM-DD)
   * @returns Un array di stringhe con i tipi di richieste attive per quel giorno
   */
  getUserRequestsForDate(user: any, date: string): string[] {
    // raccogli tutte le richieste dell’utente
    const allRequests = user.days?.flatMap((d: any) => d.requests || []) || [];

    // tieni solo quelle che coprono quella data
    const activeRequests = allRequests.filter(
      (r: any) => date >= r.start_date && date <= r.end_date
    );

    return activeRequests.map((r: any) => r.type?.trim());
  }

  /**
   * Indica se la specifica data, per un utente part-time, è marcata come
   * non lavorativa nella distribuzione oraria effettiva mostrata a calendario.
   */
  private isPartTimeNonWorkingDate(user: any, date: string): boolean {
    const calendarDay = user?.days?.find((day: any) => day?.date === date);
    const schedule = calendarDay?.part_time_schedule;

    return !!schedule?.is_part_time && !schedule?.is_working_day;
  }

  /**
   * Funzione che serve per ricavare il tipo della richiesta
   * @param type tipo di richiesta
   * @param status stato della richiesta
   * @returns stringa (tipo di richiesta)
   */
  getPermission(type: string, status: string): string {
    if (status == 'pending' || status == 'approved') {
      switch (type) {
        case 'Permesso':
          return 'Richiesta di permesso';
        case 'Ferie':
          return 'Richiesta di ferie';
        case 'Smart':
          return 'Richiesta di Smart';
        case 'Malattia':
          return 'Malattia da certificare';
        default:
          return '';
      }
    } else if (status == 'rejected') {
      switch (type) {
        case 'Permesso':
          return 'Permesso rifiutato';
        case 'Ferie':
          return 'Ferie rifiutate';
        case 'Smart':
          return 'Richiesta di Smart';
        default:
          return '';
      }
    } else if (status == 'confirmed') {
      switch (type) {
        case 'Permesso':
          return 'Permesso';
        case 'Ferie':
          return 'Ferie';
        case 'Malattia':
          return 'Malattia';
        case 'Smart':
          return 'Smart';
        case 'Review':
          return 'Review';
        default:
          return '';
      }
    }
    return '';
  }

  /**
   * funzione che serve per ricavare l'icona in base al tipo di richiesta
   * @param status stato della richiesta
   * @param type tipo della richiesta
   * @param external booleano per capire quale icona stiamo visualizzando, se quella della chips o quella del pop-over
   * @returns string dell'icona
   */
  getIcon(status: string, type: string, external: boolean = false): string {
    switch (status) {
      case 'pending':
        return 'assets/images/icon_calendar/pending.svg';
      case 'approved':
        if (external) return 'assets/images/icon_calendar/pending.svg';
        else
          switch (type) {
            case 'Malattia':
              return 'assets/images/icon_calendar/sickness.svg';
            case 'Permesso':
              return 'assets/images/icon_calendar/permission.svg';
            case 'Ferie':
              return 'assets/images/icon_calendar/vacation.svg';
            case 'Review':
              return 'assets/images/icon_calendar/review.svg';
            default:
              return '';
          }
      case 'rejected':
        return '';
      case 'confirmed':
        switch (type) {
          case 'Malattia':
            return 'assets/images/icon_calendar/sickness.svg';
          case 'Permesso':
            return 'assets/images/icon_calendar/permission.svg';
          case 'Ferie':
            return 'assets/images/icon_calendar/vacation.svg';
          case 'Review':
            return 'assets/images/icon_calendar/review.svg';
          default:
            return '';
        }
      default:
        return '';
    }
  }

  /**
   * funzione che serve per formattare le date di una richiesta
   * @param request passo la request
   * @returns controlla se è una data valida o se esiste
   */
  formatRequestDate(request: any): string {
    if (!request?.end_date) return '';

    const date = new Date(request.end_date);

    if (Number.isNaN(date.getTime())) return '';

    if (request.type === 'Review' || request.type === 'Permesso') {
      // andiamo a recuperare l'ora e impostarla nella data della richiesta
      const [hours, minutes] = request.end_time.split(':').map(Number);
      date.setHours(hours, minutes || 0, 0);
      return this.datePipe.transform(date, 'H:mm') ?? '';
    } else {
      return this.capitalizeFirst(
        this.datePipe.transform(date, 'EEE d/MM/YYYY') ?? ''
      );
    }
  }

  /**
   * Questa funzione serve per applicare una classe alla chips della richiesta
   * @param user utente che ha fatto la richiesta
   * @param request richiesta
   * @param i indice dell'array
   * @returns gestisce la lunghezza delle chips
   */
  getStyleChips(user: any, request: any, i: number, day: any): any {
    const bottom = i * 26 + 4 + 'px';
    let obj: any = { bottom };
    if (request.start_date < user.days[0].date) {
      // caso in cui la richiesta ha una data di inizio passata rispetto al lunedì della settimana corrente
      obj = {
        ...obj,
        left: this.columnUserWidth + 'px',
        width:
          192 * this.calculateTotalDays(user.days[0].date, request.end_date) +
          'px',
        'border-radius': '0px 16px 16px 0px'
      };
    } else if (request.end_date > user.days[4].date) {
      obj = {
        ...obj,
        left:
          this.columnUserWidth +
          192 *
            this.calculateStartRequest(user.days[0].date, request.start_date) +
          8 +
          'px',
        width:
          192 *
            (this.calculateTotalDays(user.days[0].date, user.days[4].date) -
              this.calculateStartRequest(
                user.days[0].date,
                request.start_date
              )) +
          'px',
        'border-radius': '16px 0px 0px 16px'
      };
    } else {
      // caso standard
      obj = {
        ...obj,
        left:
          this.columnUserWidth +
          192 *
            this.calculateStartRequest(user.days[0].date, request.start_date) +
          8 +
          'px',
        width:
          192 * this.calculateTotalDays(request.start_date, request.end_date) -
          16 +
          'px'
      };
    }
    return obj;
  }

  capitalizeFirst(text: string | null): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Apre la modale della richiesta
   * @param user Passo l'utente
   * @param date Passo la data di oggi
   * @param request Passo il body
   */
  openRequestModal(
    user: any,
    date: string,
    titleRequest?: string,
    request?: any,
    edit?: boolean
  ) {
    let componentToOpen: any;
    switch (titleRequest) {
      case 'Richiesta Permesso':
        componentToOpen = RequestPermissionComponent;
        break;
      case 'Richiesta Ferie':
        componentToOpen = HolidayRequestsComponent;
        break;
      case 'Malattia':
        componentToOpen = SicknessRequestComponent;
        break;
      case 'Gestisci presenze':
      case 'Luogo di lavoro':
        componentToOpen = WorkplaceRequestComponent;
        break;
      case 'Malattia da certificare':
        componentToOpen = SecondStepSicknessComponent;
        break;
      default:
        console.error(
          'Richiesta non trovata per title: %o, request: %o',
          titleRequest,
          request
        );
    }

    const openModalWithEventDates = (userEventDates: any[] = []) => {
      const paramsToSend = {
        request: request,
        user: user,
        date: date,
        edit: edit,
        userEventDates: userEventDates,
        canCreatePastRequest: this.canCreateRequestsForUsers(user)
      };

      const modal = this.modalService.openModal(
        componentToOpen,
        640,
        paramsToSend
      );

      // Qui uso il tipo specifico del componente
      const instance: any = modal.getContentComponent();

      if (instance?.saved?.subscribe) {
        instance.saved.subscribe((event: any) => {
          if (event.success) this.getUsersCalendar(event.date);
          else this.getActiveUsers();
        });
      }
    };
    openModalWithEventDates();
  }

  /**
   * Funzione che serve per aprire il riepilogo mensile dell'utente
   * @param user utente del riepilogo
   */
  openCalendarUser(user: any) {
    const userToSend = {
      first_name: user.first_name,
      last_name: user.last_name,
      id: user.id,
      path: user.path,
      role: user.role
    };
    this.modalService.openModal(UserCalendarComponent, 720, userToSend);
  }

  /**
   * Funzione per aprire la modale per creare una review
   * @param user utete a cui creare la richeista
   * @param date data dell'evento
   * @param request opzionale (richiesta esistente da modificare)
   */
  openModalReview(user: any, date: string, request?: any) {
    const paramToSend = {
      request: request,
      user: user,
      date: date
    };
    const modal = this.modalService.openModal(
      ReviewModalComponent,
      640,
      paramToSend
    );

    const instance = modal.getContentComponent();
    // Sottoscrivi subito l'evento
    instance.notifyPartent.subscribe(() => {
      this.getUsersCalendar(this.selectedWeek?.startWeek);
      modal.close();
    });
  }

  /**
   * funzione che serve ad aprire le modali per creare delle richieste di: Permesso, Ferie, Malattia e cambio smart
   * @param user utente che sta creando la richiesta
   * @param date data di quando stiamo creando la richiesta
   * @param request apro le modali di modifica in base al ruolo e alla tipologia di richiesta
   */
  openModal(user: any, date: string, request?: any, forceEdit = false) {
    if (request?.type === 'Review') {
      this.openModalReview(user, date, request);
    }
    // se sono admin o project manager e non è una review posso approvare direttamente
    if (
      !forceEdit &&
      (((this.userRole == 'admin' || this.canHrConfirmRequest(request, user)) &&
        request.type != 'Review') ||
        (this.userRole != 'hr_manager' &&
          !this.isUserLoggedRequest(request) &&
          request.type != 'Review'))
    ) {
      const payload = {
        event_id: request.id,
        approved: 1,
        event_type_id: request.event_type_id
      };
      this.calendarService
        .manageUserRequest(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.getUsersCalendar(this.selectedWeek?.startWeek);
          }
        });
    } else {
      switch (request.type) {
        case 'Permesso':
          return this.openRequestModal(
            user,
            date,
            'Richiesta Permesso',
            request,
            true
          );
        case 'Ferie':
          return this.openRequestModal(
            user,
            date,
            'Richiesta Ferie',
            request,
            true
          );
        case 'Smart intera giornata':
        case 'Smart mattina':
        case 'Smart pomeriggio':
          return this.openRequestModal(
            user,
            date,
            'Luogo di lavoro',
            request,
            true
          );
        case 'Malattia':
          return this.openRequestModal(
            user,
            date,
            'Malattia da certificare',
            request,
            true
          );
        default:
          return '';
      }
    }
  }

  /**
   * Metodo che serve per dare o meno la possibilità di modificare o eliminare una richiesta.
   * Per Ferie e Permesso, l'azione è permessa solo se lo stato è 'pending' o 'confirmed'.
   *
   * @param request Richiesta da verificare
   * @returns boolean true se è possibile modificare o eliminare la richiesta, altrimenti false
   */
  canForceManageEvents(): boolean {
    return this.userRole == 'admin' || this.userRole == 'hr_manager';
  }

  canEditOrDelete(request: any): boolean {
    if (!request) return false;

    if (this.canForceManageEvents()) {
      return true;
    }

    if (request.type === 'Ferie' || request.type === 'Permesso') {
      return request.status === 'pending' || request.status === 'confirmed';
    }

    return false;
  }

  canShowEditDeleteButtons(request: any): boolean {
    if (this.canForceManageEvents()) {
      return (
        request?.type != 'Review' &&
        (this.canEditEvent(request) || this.canDeleteEvent(request))
      );
    }

    return (
      this.isUserLoggedRequest(request) &&
      request.type != 'Review' &&
      this.canEditOrDelete(request)
    );
  }

  canManageCertifiedSickness(request: any): boolean {
    return (
      this.canForceManageEvents() &&
      request?.type === 'Malattia' &&
      request?.status === 'confirmed'
    );
  }

  canEditEvent(request: any): boolean {
    if (this.canManageCertifiedSickness(request)) {
      return false;
    }

    return this.canEditOrDelete(request);
  }

  canDeleteEvent(request: any): boolean {
    if (this.canManageCertifiedSickness(request)) {
      return false;
    }

    return this.canEditOrDelete(request);
  }

  /**
   * Metodo che serve per aprire la modale di cancellazione di un evento.
   * Se l'utente è admin e la richiesta non è una review, viene eliminata direttamente.
   * Altrimenti viene aperta la modale di cancellazione.
   * @param user utente che sta creando la richiesta
   * @param request richiesta da eliminare
   */
  deleteEventModal(user: any, request: any, forceDelete = false) {
    if (this.userRole == 'admin' && request.type != 'Review' && !forceDelete) {
      const payload = {
        event_id: request.id,
        approved: 0,
        event_type_id: 7
      };
      this.calendarService
        .manageUserRequest(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.getUsersCalendar(this.selectedWeek?.startWeek);
          }
        });
    } else {
      const paramsToSend = {
        user: user,
        request: request,
        startDate: new Date(request.start_date),
        endDate: new Date(request.end_date)
      };

      const modal = this.modalService.openModal(
        DeleteEventModalComponent,
        640,
        paramsToSend
      );

      const instance = modal.getContentComponent();
      // Sottoscrivi subito l'evento
      instance.notifyPartent.subscribe(() => {
        this.getUsersCalendar(this.selectedWeek?.startWeek);
        modal.close();
      });
    }
  }

  /**
   * Metodo che viene eseguito quando selezioniamo un utente dalla multiselect.
   * @param event utente selezionato
   */
  onSelectUser(event: any) {
    this.usersSelectedSubject.next(event);
    this.monthSelected = this.selectedWeek.startWeek;
    this.usersSelected = event;
  }

  /**
   * Metodo che serve per aprire la sidebar del calendario.
   */
  openSidebarCalendar() {
    this.isSideOpen = true;
  }

  /**
   * Metodo che serve per chiudere la sidebar del calendario.
   */
  onSidebarClosed() {
    this.isSideOpen = false;
  }

  /**
   * Metodo che viene eseguito quando selezioniamo un/dei team dalla sidebar
   * @param teamsIdSelected id dei team selezionato
   */
  onTeamsChange(teamsIdSelected: any[]) {
    this.teamsIdSelected = teamsIdSelected;
    this.monthSelected = this.selectedWeek.startWeek;
    this.getUsersCalendar(this.selectedWeek?.startWeek);
  }

  /**
   * Metodo che viene eseguito quando selezioniamo un/dei ruolo/i dalla sidebar
   * @param rolesSelected id dei ruoli selezionati
   */
  onRolesChange(rolesSelected: any[]) {
    this.roleIdSelected = rolesSelected;
    this.monthSelected = this.selectedWeek.startWeek;
    this.getUsersCalendar(this.selectedWeek?.startWeek);
  }

  /**
   * Metodo che viene eseguito quando selezioniamo un/dei evento/i dalla sidebar
   * @param eventsIdSelected id degli eventi selezionati
   */
  onEventsChange(eventsIdSelected: any[]) {
    this.eventsIdSelected = eventsIdSelected;
    this.monthSelected = this.selectedWeek.startWeek;
    this.getUsersCalendar(this.selectedWeek?.startWeek);
  }

  /**
   * Metodo che viene eseguito quando cambiamo la settimana
   * @param weekSelected settimana selezionata
   */
  onDateChange(weekSelected: { startWeek: Date; endWeek: Date }) {
    this.selectedWeek.startWeek = weekSelected.startWeek;
    this.selectedWeek.endWeek = weekSelected.endWeek;

    this.getUsersCalendar(weekSelected.startWeek);
  }

  /**
   * è una funzione che serve solo per ricavare la dimensione della prima colonna ogni volta
   * che facciamo una chiamata che richiami la index
   */
  updateWidth() {
    setTimeout(() => {
      this.setColumnWidthIfAvailable();
    }, 100);
  }

  /**
   * Prende tutti gli eventi dell’utente.
   * Controlla quali di questi coprono il giorno che stiamo renderizzando.
   * Ritorna solo quelli validi, così li puoi mostrare nel template senza sovrapposizioni.
   * @param user utente da controllare
   * @param day giorno da ciclare
   */
  getRequestsForDay(user: any, day: any) {
    // raccogli tutti gli eventi dell’utente (da tutti i giorni)
    const allRequests = user.days.flatMap((d: any) => d.requests || []);

    // ritorna solo quelli che attraversano il giorno specifico
    return allRequests.filter(
      (req: any) =>
        day.date >= req.start_date &&
        day.date <= req.end_date &&
        !req.type?.includes('Smart') &&
        !req.type?.includes('Presenza')
    );
  }

  /**
   * Funzione che abilita o disabilita i bottoni di modifica ed elimina in base al ruolo e allo stato della richiesta
   * @param request richiesta da controllare
   * @returns boolean se abilitare l'accettazione o la modifica della richiesta
   *
   * 1° controllo: L’admin può sempre agire sulle richieste di tipo Review, indipendentemente dallo stato o da chi le ha create.
   *
   * 2° controllo:
   *  l’admin può agire;
   *  il project manager può agire SOLO se:
   *     NON è una sua richiesta
   *     NON è una Review
   *     In entrambi i casi sopra, la richiesta non deve essere confermata.
   */
  enableTypeBtn(request: any, user?: any): boolean {
    if (
      (this.userRole == 'admin' && request.type == 'Review') ||
      ((this.userRole == 'admin' ||
        this.canHrConfirmRequest(request, user) ||
        (this.userRole == 'project_manager' &&
          !this.isUserLoggedRequest(request) &&
          request.type != 'Review' &&
          request.status != 'approved')) &&
        request.status != 'confirmed')
    ) {
      return true;
    }
    return false;
  }

  canShowManagementButtons(request: any, user: any): boolean {
    if (this.userRole == 'admin') {
      return this.enableTypeBtn(request, user);
    }

    if (this.userRole == 'hr_manager') {
      return this.canHrConfirmRequest(request, user);
    }

    return (
      this.userRole != 'user' &&
      this.enableTypeBtn(request, user) &&
      !this.isUserLoggedAcceptedRequest(request.approvals)
    );
  }

  canShowPmContainer(request: any, user: any): boolean {
    if (request?.type == 'Review') return false;

    const canViewRequestInfo =
      this.isUserLoggedRequest(request) ||
      this.userRole === 'project_manager' ||
      this.userRole === 'admin' ||
      this.userRole === 'hr_manager';

    if (!canViewRequestInfo) return false;

    return (
      request?.approvals?.length > 0 ||
      request?.status === 'approved' ||
      this.isUserLoggedAcceptedRequest(request?.approvals) ||
      (request?.status === 'approved' &&
        (this.userRole == 'admin' || this.canHrConfirmRequest(request, user)))
    );
  }

  canHrConfirmRequest(request: any, user?: any): boolean {
    return (
      this.userRole == 'hr_manager' &&
      request?.status == 'approved' &&
      request?.type != 'Review' &&
      !this.isUserLoggedRequest(request)
    );
  }

  isProjectManagerUser(user: any): boolean {
    const role = String(user?.role ?? '')
      .toLowerCase()
      .replace('_', ' ');

    return role == 'project manager';
  }

  /**
   * Funzione che serve a capire se la request appartiene all'utente loggato
   * @param request request da controllare
   * @returns boolean se la request appartiene all'utente loggato
   */
  isUserLoggedRequest(request: any): boolean {
    const currentUser = this.users.find(u => u.id === this.profileData.id);

    if (!currentUser) return false;

    const userRequests =
      currentUser.days?.flatMap((d: any) => d.requests || []) || [];

    return userRequests.some((req: any) => req.id === request.id);
  }

  /**
   * questa funzione serve per recuperare quale classi dobbiamo inserire per l'icona dello smart
   * @param day giorno da controllare
   * @returns string (classi per l'icona)
   */
  getSmartClass(day: any, user: any): string {
    const partTimeSchedule = day?.part_time_schedule;

    if (
      partTimeSchedule?.is_part_time &&
      !this.hasBlockingAbsenceRequest(day, user) &&
      this.isPartTimeSmartDay(day, user)
    ) {
      const colorClass =
        partTimeSchedule?.status === 'pending' &&
        partTimeSchedule?.pending_changed
          ? 'orange-400'
          : 'blue-700';
      const cursorClass =
        partTimeSchedule?.status === 'pending' &&
        partTimeSchedule?.pending_changed
          ? 'cursor-pointer'
          : '';
      const startTime = this.normalizePartTimeBadgeTime(
        partTimeSchedule?.start_time
      );
      const endTime = this.normalizePartTimeBadgeTime(
        partTimeSchedule?.end_time
      );

      if (startTime === '09:00') {
        return `icons holiday-smart fill-svg-${colorClass} ${cursorClass}`;
      }

      if (endTime === '18:00') {
        return `icons afternoon-smart fill-svg-${colorClass} ${cursorClass}`;
      }

      return `icons smart fill-svg-${colorClass} ${cursorClass}`;
    }

    const smartRequest = this.getSmartRequest(day, user);

    // se esiste una richiesta
    if (smartRequest) {
      const isPending = smartRequest.status === 'pending';
      const colorClass = isPending ? 'orange-400' : 'blue-700';
      const cursorClass = isPending ? 'cursor-pointer' : '';

      switch (smartRequest.type) {
        case 'Smart intera giornata':
          return `icons smart fill-svg-${colorClass} ${cursorClass}`;
        case 'Smart mattina':
          return `icons holiday-smart fill-svg-${colorClass} ${cursorClass}`;
        case 'Smart pomeriggio':
          return `icons afternoon-smart fill-svg-${colorClass} ${cursorClass}`;
        case 'Presenza':
          // Se la richiesta di presenza è pending, mostra l'icona office in arancione
          if (isPending) {
            return `icons office fill-svg-${colorClass} ${cursorClass}`;
          }
          return '';
        default:
          // Se il tipo è diverso ma è comunque una richiesta smart/presenza
          if (smartRequest.type?.includes('Smart')) {
            return `icons smart fill-svg-${colorClass} ${cursorClass}`;
          }
          return '';
      }
    }

    // nessuna richiesta → fallback sul workMode (comportamento attuale)
    switch (day.workMode) {
      case 2:
        return 'icons smart fill-svg-blue-700';
      case 3:
        return 'icons holiday-smart fill-svg-blue-700';
      case 4:
        return 'icons afternoon-smart fill-svg-blue-700';
      default:
        return '';
    }
  }

  /**
   * Restituisce il tooltip smart solo nei casi part-time in cui l'icona smart
   * sta rappresentando l'orario lavorativo della giornata.
   *
   * Per tutti gli altri utenti lasciamo invariata la UX precedente, quindi
   * non forziamo alcun tooltip dedicato.
   */
  getSmartTooltip(day: any, user: any): string {
    const partTimeSchedule = day?.part_time_schedule;

    if (
      partTimeSchedule?.is_part_time &&
      !this.hasBlockingAbsenceRequest(day, user) &&
      this.isPartTimeSmartDay(day, user)
    ) {
      return this.buildPartTimeBadgeTooltip(partTimeSchedule);
    }

    return '';
  }

  /**
   * Costruisce il badge part-time da mostrare nella griglia calendario.
   *
   * Regole attuali:
   * - se esiste una ridistribuzione pending, il badge usa quella
   * - altrimenti usa la settimana confermata/di contratto
   * - i giorni con ferie, permesso o malattia non mostrano badge part-time
   * - per ora smart e ufficio condividono gli stessi asset grafici
   */
  getPartTimeBadge(
    day: any,
    user: any
  ): {
    src: string;
    tooltip: string;
    status: 'pending' | 'confirmed';
    requestStatus: 'pending' | 'confirmed';
    requestId: string | null;
    variant:
      | 'non_working'
      | 'office_full'
      | 'office_morning'
      | 'office_afternoon'
      | 'smart_morning'
      | 'smart_afternoon';
  } | null {
    const schedule = day?.part_time_schedule;

    if (!schedule?.is_part_time) {
      return null;
    }

    if (this.hasBlockingAbsenceRequest(day, user)) {
      return null;
    }

    // La logica smart esistente ha priorità visiva sui badge part-time:
    // se il giorno ha già una richiesta/evento smart, continuiamo a mostrare
    // l'icona smart storica invece del badge di distribuzione oraria.
    if (this.hasSmartVisualPriority(day, user)) {
      return null;
    }

    const requestStatus: 'pending' | 'confirmed' =
      schedule?.status === 'pending' ? 'pending' : 'confirmed';

    const status: 'pending' | 'confirmed' =
      schedule?.status === 'pending' && schedule?.pending_changed
        ? 'pending'
        : 'confirmed';

    if (!schedule?.is_working_day) {
      return {
        src: this.getPartTimeBadgeAsset('non_working', status),
        tooltip: this.buildPartTimeBadgeTooltip(schedule),
        status,
        requestStatus,
        requestId: schedule?.request_id ?? null,
        variant: 'non_working'
      };
    }

    const startTime = this.normalizePartTimeBadgeTime(schedule?.start_time);
    const endTime = this.normalizePartTimeBadgeTime(schedule?.end_time);
    const isSmartDay = this.isPartTimeSmartDay(day, user);

    if (!isSmartDay && startTime === '09:00' && endTime === '18:00') {
      return {
        src: this.getPartTimeBadgeAsset('office_full', status),
        tooltip: this.buildPartTimeBadgeTooltip(schedule),
        status,
        requestStatus,
        requestId: schedule?.request_id ?? null,
        variant: 'office_full'
      };
    }

    if (startTime === '09:00') {
      const variant = isSmartDay ? 'smart_morning' : 'office_morning';

      return {
        src: this.getPartTimeBadgeAsset(variant, status),
        tooltip: this.buildPartTimeBadgeTooltip(schedule),
        status,
        requestStatus,
        requestId: schedule?.request_id ?? null,
        variant
      };
    }

    if (endTime === '18:00') {
      const variant = isSmartDay ? 'smart_afternoon' : 'office_afternoon';

      return {
        src: this.getPartTimeBadgeAsset(variant, status),
        tooltip: this.buildPartTimeBadgeTooltip(schedule),
        status,
        requestStatus,
        requestId: schedule?.request_id ?? null,
        variant
      };
    }

    return null;
  }

  /**
   * Indica se l'utente ha almeno un giorno della settimana marcato come
   * part-time nel payload calendario.
   *
   * Ci serve solo per mostrare il badge fisso "PT" sopra l'avatar utente.
   */
  isPartTimeUser(user: any): boolean {
    return !!user?.days?.some(
      (day: any) => day?.part_time_schedule?.is_part_time
    );
  }

  /**
   * Carica e memorizza in cache il dettaglio della richiesta pending part-time
   * così il popover può mostrare la settimana completa, la nota e le azioni.
   */
  loadPartTimePendingRequest(user: any, day: any): void {
    const requestId = day?.part_time_schedule?.request_id;
    const requestStatus = day?.part_time_schedule?.status;

    if (!requestId || requestStatus !== 'pending') {
      return;
    }

    const requestKey = String(requestId);

    if (
      this.partTimePendingRequestCache.has(requestKey) ||
      this.loadingPartTimePendingRequests.has(requestKey)
    ) {
      return;
    }

    this.loadingPartTimePendingRequests.add(requestKey);

    this.requestService
      .getPartTimeWeekChangeHistory(user.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const request = response?.result?.find(
            (item: any) => String(item.id) === requestKey
          );

          if (request) {
            this.partTimePendingRequestCache.set(requestKey, request);
          }

          this.loadingPartTimePendingRequests.delete(requestKey);
        },
        error: () => {
          this.loadingPartTimePendingRequests.delete(requestKey);
        }
      });
  }

  /**
   * Restituisce dal cache FE il dettaglio della richiesta pending part-time
   * collegata al giorno cliccato.
   */
  getPartTimePendingRequest(day: any): any | null {
    const requestId = day?.part_time_schedule?.request_id;
    if (!requestId) return null;

    return this.partTimePendingRequestCache.get(String(requestId)) ?? null;
  }

  /**
   * Dice se il badge part-time deve aprire il popover di richiesta settimanale.
   *
   * Anche i giorni rimasti blu in una settimana pending devono aprire il
   * riepilogo, perché appartengono comunque alla stessa richiesta settimanale.
   */
  canOpenPartTimePendingPopover(day: any): boolean {
    return (
      !!day?.part_time_schedule?.request_id &&
      day?.part_time_schedule?.status === 'pending'
    );
  }

  /**
   * Abilita le azioni di approvazione/rifiuto sulla richiesta part-time
   * settimanale solo per admin e hr_manager, escludendo il richiedente.
   */
  canManagePartTimePendingRequest(request: any): boolean {
    return (
      !!request &&
      (this.userRole === 'admin' || this.userRole === 'hr_manager') &&
      String(request.user_id) !== String(this.profileData?.id)
    );
  }

  /**
   * Approva la richiesta pending part-time usando l'endpoint dedicato.
   */
  approvePartTimePendingRequest(request: any): void {
    if (!request?.id) return;

    this.requestService
      .approvePartTimeWeekChange(request.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.partTimePendingRequestCache.delete(String(request.id));
          this.getUsersCalendar(this.selectedWeek?.startWeek);
        }
      });
  }

  /**
   * Apre la modale motivazione e, se confermata, rifiuta la richiesta pending
   * part-time usando l'endpoint dedicato.
   */
  openModalRejectPartTimePendingRequest(request: any): void {
    const modal = this.nzModalService.create({
      nzContent: ModalRejectedRequestComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '640px',
      nzClassName: 'custom-modal-reject-request',
      nzComponentParams: {}
    });

    modal.afterClose.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      if (event == undefined || event == null || event == '') {
        return;
      }

      this.requestService
        .rejectPartTimeWeekChange(request.id, {
          rejection_reason: event
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.partTimePendingRequestCache.delete(String(request.id));
            this.getUsersCalendar(this.selectedWeek?.startWeek);
          }
        });
    });
  }

  /**
   * Restituisce l'etichetta FE del giorno della settimana usata nel riepilogo
   * popover part-time.
   */
  getWeekdayLabel(weekday: number): string {
    const labels: Record<number, string> = {
      1: 'Lunedì',
      2: 'Martedì',
      3: 'Mercoledì',
      4: 'Giovedì',
      5: 'Venerdì'
    };

    return labels[weekday] || '';
  }

  /**
   * Restituisce l'orario FE già formattato di un giorno della distribuzione
   * part-time, sia per la colonna default sia per la colonna richiesta.
   */
  getPartTimeScheduleLabel(scheduleDay: any): string {
    if (!scheduleDay?.is_working_day) {
      return 'Non lavorativo';
    }

    const startTime = this.normalizePartTimeBadgeTime(scheduleDay?.start_time);
    const endTime = this.normalizePartTimeBadgeTime(scheduleDay?.end_time);

    if (startTime && endTime) {
      return `${startTime} - ${endTime}`;
    }

    return '';
  }

  /**
   * Recupera il giorno base della settimana "default" corrispondente al
   * weekday della richiesta pending mostrata nel popover.
   */
  getDefaultPartTimeScheduleDay(request: any, weekday: number): any | null {
    return (
      request?.default_days?.find((day: any) => day?.weekday === weekday) ??
      null
    );
  }

  /**
   * Questa funzione serve a ricavare la richiesta smart/presenza prioritaria in un singolo giorno
   * Priorità: pending > altre
   * @param day giorno da controllare
   * @returns richiesta smart o presenza
   */
  getSmartRequest(day: any, user: any) {
    if (!user?.days) return null;

    // raccogli tutti gli eventi dell’utente (da tutti i giorni) per gestire i multi-giorno
    const allRequests = user.days.flatMap((d: any) => d.requests || []);

    const smartRequests = allRequests.filter(
      (request: any) =>
        (request.type?.includes('Smart') || request.type === 'Presenza') &&
        day.date >= request.start_date &&
        day.date <= request.end_date
    );

    if (!smartRequests || smartRequests.length === 0) {
      return null;
    }

    // priorità alle pending
    const pendingRequest = smartRequests.find(
      (request: any) => request.status === 'pending'
    );

    // se non ci sono richieste in pendig ritorna la prima richiesta disponibile (es. confermata)
    if (pendingRequest) {
      return pendingRequest;
    }

    // prima richiesta disponibile (che sarà confermata se non ci sono pending)
    return smartRequests[0];
  }

  /**
   * Nasconde il badge part-time quando sul giorno esiste un'assenza che ha già
   * una propria rappresentazione in calendario.
   */
  private hasBlockingAbsenceRequest(day: any, user: any): boolean {
    if (!user?.days || !day?.date) {
      return false;
    }

    const allRequests = user.days.flatMap(
      (calendarDay: any) => calendarDay.requests || []
    );

    return allRequests.some(
      (request: any) =>
        ['Ferie', 'Permesso', 'Malattia'].includes(request?.type) &&
        day.date >= request.start_date &&
        day.date <= request.end_date
    );
  }

  /**
   * Mantiene prioritaria la rappresentazione smart già esistente nel calendario.
   *
   * Se il giorno è già smart tramite richiesta o workMode confermato, il
   * badge part-time non va mostrato.
   */
  private hasSmartVisualPriority(day: any, user: any): boolean {
    return (
      !!this.getSmartRequest(day, user) || [2, 3, 4].includes(day?.workMode)
    );
  }

  /**
   * Capisce se il giorno part-time va rappresentato come smart.
   *
   * Prima guardiamo eventuali richieste smart del giorno; se non esistono
   * facciamo fallback sul workMode confermato presente nel payload calendario.
   */
  private isPartTimeSmartDay(day: any, user: any): boolean {
    const smartRequest = this.getSmartRequest(day, user);

    if (smartRequest) {
      return smartRequest.type?.includes('Smart');
    }

    return [2, 3, 4].includes(day?.workMode);
  }

  /**
   * Uniforma gli orari del payload part-time nel formato HH:mm per i controlli
   * mattina/pomeriggio.
   */
  private normalizePartTimeBadgeTime(time: string | null | undefined): string {
    return String(time || '').slice(0, 5);
  }

  /**
   * Restituisce il path dell'asset badge part-time in base a variante e stato.
   *
   * In questo primo step smart e ufficio condividono gli stessi SVG.
   */
  private getPartTimeBadgeAsset(
    variant: string,
    status: 'pending' | 'confirmed'
  ): string {
    const iconMap: Record<string, { pending: string; confirmed: string }> = {
      non_working: {
        confirmed:
          'assets/images/icon_calendar/part_time_non_working_confirmed.svg',
        pending: 'assets/images/icon_calendar/part_time_non_working_pending.svg'
      },
      office_full: {
        confirmed: 'assets/images/icon_calendar/office.svg',
        pending: 'assets/images/icon_calendar/part_time_office_full_pending.svg'
      },
      office_morning: {
        confirmed:
          'assets/images/icon_calendar/part_time_office_morning_confirmed.svg',
        pending:
          'assets/images/icon_calendar/part_time_office_morning_pending.svg'
      },
      office_afternoon: {
        confirmed:
          'assets/images/icon_calendar/part_time_office_afternoon_confirmed.svg',
        pending:
          'assets/images/icon_calendar/part_time_office_afternoon_pending.svg'
      },
      smart_morning: {
        confirmed:
          'assets/images/icon_calendar/part_time_office_morning_confirmed.svg',
        pending:
          'assets/images/icon_calendar/part_time_office_morning_pending.svg'
      },
      smart_afternoon: {
        confirmed:
          'assets/images/icon_calendar/part_time_office_afternoon_confirmed.svg',
        pending:
          'assets/images/icon_calendar/part_time_office_afternoon_pending.svg'
      }
    };

    return iconMap[variant]?.[status] || '';
  }

  /**
   * Costruisce il tooltip dei badge part-time usando l'orario effettivo
   * restituito per quella giornata dal payload calendario.
   */
  private buildPartTimeBadgeTooltip(schedule: any): string {
    if (!schedule?.is_working_day) {
      return 'Non lavorativo';
    }

    const startTime = this.normalizePartTimeBadgeTime(schedule?.start_time);
    const endTime = this.normalizePartTimeBadgeTime(schedule?.end_time);

    if (startTime && endTime) {
      return `${startTime} - ${endTime}`;
    }

    return '';
  }

  /*
   * Funzione che ritorna il messaggio da visualizzare nella popover in base al ruolo e alla tipologia di richiesta
   * @param type tipo di richiesta
   * @param status stato della richiesta
   * @returns stringa del messaggio da visualizzare
   */
  getMessageRequest(type: string, status: string): string {
    if (type != 'Riview' && type != 'Malattia' && this.userRole == 'user')
      return status == 'pending'
        ? "In attesa di una conferma dai PM o dall'Admin"
        : "In attesa di conferma dall'amministrazione";
    return "In attesa di una risposta dall'amministrazione.";
  }

  /**
   * Funzione per aprire la modale per rifiutare una richiesta
   * @param request richiesta da rifiutare
   */
  openModalRejectRequest(request: any) {
    const modal = this.nzModalService.create({
      nzContent: ModalRejectedRequestComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '640px',
      nzClassName: 'custom-modal-reject-request',
      nzComponentParams: {}
    });

    modal.afterClose.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      const payload = {
        event_id: request.id,
        approved: 0,
        event_type_id: request.event_type_id,
        motivation: event
      };
      if (event != undefined && event != null && event != '') {
        this.calendarService
          .manageUserRequest(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.getUsersCalendar(this.selectedWeek?.startWeek);
            }
          });
      }
    });
  }

  /**
   * Metodo che serve per recuperare tutte le richieste approvate dell'utente loggato
   * @param approvals richieste
   * @returns boolean
   */
  isUserLoggedAcceptedRequest(approvals: any[]): boolean {
    return approvals.some(approval => {
      return (
        String(approval.user_id) === String(this.profileData.id) &&
        this.isApprovalAccepted(approval.is_approved)
      );
    });
  }

  private isApprovalAccepted(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
  }

  /**
   * Metodo che serve per spostarsi di 1 settimana in avanti o indietro
   *
   * @param delta (number) di quante settimane spostarsi indietro(-1)/avanti(1)
   */
  moveWeek(delta: number) {
    const base = moment(this.selectedWeek.startWeek, 'DD-MM-YYYY').startOf(
      'isoWeek'
    );
    const start = base.clone().add(delta, 'week');

    this.selectedWeek = {
      startWeek: start.toDate(),
      endWeek: start.clone().isoWeekday(5).toDate()
    };

    this.monthSelected = start.toDate();

    this.getUsersCalendar(start.toDate());
  }

  /**
   * Metodo che richiama l'API per recuperare tutte le notitiche appartenete all'utente loggato
   */
  getNotifications(monthSelected?: Date) {
    this.calendarService
      .getNotifications(
        moment(monthSelected ?? this.selectedWeek.startWeek).format(
          'YYYY/MM/DD'
        ),
        this.calendarArea
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.notifications = res;
        }
      });
  }

  /**
   * Metodo che serve per filtrare la visualizzazione del calendario con gli utenti che hanno una richiesta in pending,
   * @param event emit che manda ids e startWeekDate degli utenti che hann ouna richeista in pending per una determinata settimana
   */
  onFilterUsersRequest(event: { usersId: string[]; startWeek: Date }) {
    this.usersSelected = event.usersId;
    this.monthSelected = event.startWeek;
    this.getUsersCalendar(event.startWeek);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
